import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import type { LangStore } from "../../../store/LangStore";

/**
 * El chunk del visor no se pudo bajar: una pestaña abierta antes de un deploy
 * pide un archivo que el nginx nuevo ya no tiene. Reintentar no sirve (React.lazy
 * recuerda el rechazo); hay que recargar la página.
 */
export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk/.test(error.message)
  );
}

export type ContractFieldReviewErrorBoundaryProps = {
  /** Si el diálogo está abierto: el aviso se cierra con él. */
  open: boolean;
  onClose: () => void;
  langStore: LangStore;
  children: React.ReactNode;
};

type State = { error: Error | null };

/**
 * Deja cualquier falla del visor adentro de su diálogo: el chunk que no baja,
 * o pdf.js que no arranca en un navegador viejo (react-pdf llama a
 * `getDocument` en un efecto, sin try/catch). Sin esto el error sube hasta el
 * boundary de la ruta y la vista entera de la edición firmada se reemplaza por
 * "Ocurrió un error inesperado".
 *
 * Quien lo usa le cambia la `key` en cada apertura: así cada apertura parte de
 * cero y vuelve a intentar.
 */
export class ContractFieldReviewErrorBoundary extends React.Component<
  ContractFieldReviewErrorBoundaryProps,
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  componentDidCatch(error: unknown): void {
    console.error("Revisión de campos: el visor falló", error);
  }

  render(): React.ReactNode {
    const { error } = this.state;
    const { open, onClose, langStore, children } = this.props;
    if (!error) return children;
    const outdated = isChunkLoadError(error);
    return (
      <Dialog open={open} onClose={onClose} sx={{ zIndex: 10350 }}>
        <DialogContent>
          <Alert severity="error">
            {outdated
              ? langStore.contractFieldsReloadPage
              : langStore.contractFieldsLoadError}
          </Alert>
        </DialogContent>
        <DialogActions>
          {outdated && (
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              {langStore.contractFieldsReloadButton}
            </Button>
          )}
          <Button onClick={onClose}>{langStore.closeCommand}</Button>
        </DialogActions>
      </Dialog>
    );
  }
}
